import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table, TableHead, TableBody, TableCell, TableRow, TableContainer,
  Paper, IconButton, Menu, MenuItem, TextField, Box, Typography,
  Button, InputAdornment, Checkbox, Toolbar, Tooltip, Avatar,
  TablePagination, TableSortLabel, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Snackbar, Alert, Skeleton, 
  Drawer, Divider, Grid, Tabs, Tab, Chip
} from "@mui/material";

// Modern Feather Icons
import { 
  FiSearch, FiFilter, FiMoreHorizontal, FiTrash2, FiCheckCircle, 
  FiXCircle, FiDownload, FiUser, FiActivity, FiClock, 
  FiUsers, FiAlertCircle, FiChevronRight, FiRefreshCw
} from "react-icons/fi";

import { fetchUsers, updateUserStatus, deleteUser } from "../../api/adminApi";
import "./UserTable.css";

// --- 1. Optimized Stat Card Component (Prevents Re-renders) ---
const StatCard = React.memo(({ title, value, icon, color, subtext }) => (
  <Paper className="glass-card" sx={{ p: 3, position: 'relative', overflow: 'hidden' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 1 }}>{title}</Typography>
        <Typography variant="h4" fontWeight={700} sx={{ color: '#0f172a' }}>{value}</Typography>
        {subtext && <Typography variant="caption" sx={{ color: color, bgcolor: `${color}15`, px: 1, py: 0.5, borderRadius: 1, mt: 1, display: 'inline-block', fontWeight: 600 }}>{subtext}</Typography>}
      </Box>
      <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: `${color}15`, color: color, display: 'flex' }}>
        {icon}
      </Box>
    </Box>
  </Paper>
));

// --- 2. Main Component ---
const UserTable = () => {
  // Data State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter & Sort State
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  
  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);

  // Selection & UI State
  const [selected, setSelected] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUserId, setMenuUserId] = useState(null);
  
  // Drawer & Dialog State
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tabValue, setTabValue] = useState(0); // For Drawer Tabs
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null, type: '' });
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });

  // --- API Loading ---
  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchUsers();
      setUsers(res.data || []);
    } catch (err) {
      showSnackbar("Failed to fetch data", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // --- Stats Calculation (Memoized) ---
  const stats = useMemo(() => {
    const total = users.length;
    const pending = users.filter(u => u.status === 'pending').length;
    const active = users.filter(u => u.status === 'approved').length;
    const rejected = users.filter(u => u.status === 'rejected').length;
    return { total, pending, active, rejected };
  }, [users]);

  // --- Filtering Logic (Memoized) ---
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const matchSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          u.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = statusFilter === 'all' || u.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [users, searchTerm, statusFilter]);

  // --- Handlers (Callback Optimized) ---
  const handleRequestSort = useCallback((property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  }, [order, orderBy]);

  const handleAction = useCallback(async (id, action) => {
    try {
      if (action === "delete") {
        await deleteUser(id);
        setUsers(prev => prev.filter(u => u.id !== id));
        showSnackbar("User deleted successfully", "success");
      } else {
        const status = action === "approve" ? "approved" : "rejected";
        await updateUserStatus(id, status);
        setUsers(prev => prev.map(u => u.id === id ? { ...u, status } : u));
        showSnackbar(`User status updated to ${status}`, "success");
        if (selectedUser?.id === id) setSelectedUser(prev => ({ ...prev, status }));
      }
    } catch (err) {
      showSnackbar("Operation failed", "error");
    }
    setAnchorEl(null);
    setConfirmDialog(prev => ({ ...prev, open: false }));
  }, [selectedUser]);

  const handleBulkDelete = async () => {
    setUsers(prev => prev.filter(u => !selected.includes(u.id)));
    setSelected([]);
    showSnackbar(`${selected.length} users deleted`, "success");
  };

  const handleExport = () => {
    const csvContent = "data:text/csv;charset=utf-8," + 
      ["Name,Email,Status,Date"].concat(filteredUsers.map(u => `${u.name},${u.email},${u.status},${u.created_at}`)).join("\n");
    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = "users_export.csv";
    link.click();
  };

  const showSnackbar = (msg, type) => setSnackbar({ open: true, msg, type });

  // --- Helpers ---
  const getStatusBadge = (status) => {
    const styles = {
      approved: "status-approved",
      rejected: "status-rejected",
      pending: "status-pending"
    };
    return (
      <span className={`status-dot-badge ${styles[status] || ''}`}>
        <span className="status-dot"></span>
        {status}
      </span>
    );
  };

  const stringToColor = (string) => {
    let hash = 0;
    for (let i = 0; i < string.length; i++) hash = string.charCodeAt(i) + ((hash << 5) - hash);
    let color = '#';
    for (let i = 0; i < 3; i++) color += `00${((hash >> (i * 8)) & 0xff).toString(16)}`.slice(-2);
    return color;
  };

  // --- Sorting Utilities ---
  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => (b[orderBy] < a[orderBy] ? -1 : 1)
      : (a, b) => (a[orderBy] < b[orderBy] ? -1 : 1);
  };

  return (
    <Box className="dashboard-container" sx={{ width: '100%', p: 1 }}>
      
      {/* 1. Header & Widgets */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>Users Overview</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Total Users" value={stats.total} icon={<FiUsers size={24} />} color="#3b82f6" subtext="+12% this month" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Pending Requests" value={stats.pending} icon={<FiClock size={24} />} color="#f59e0b" subtext="Requires attention" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Active Students" value={stats.active} icon={<FiCheckCircle size={24} />} color="#10b981" subtext="Currently enrolled" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Rejected" value={stats.rejected} icon={<FiXCircle size={24} />} color="#ef4444" subtext="Failed verification" />
          </Grid>
        </Grid>
      </Box>

      {/* 2. Main Table Card */}
      <Paper className="glass-card" sx={{ overflow: 'hidden', mb: 4 }}>
        
        {/* Toolbar */}
        <Toolbar sx={{ p: 2, gap: 2, borderBottom: '1px solid #e2e8f0' }}>
          {selected.length > 0 ? (
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', bgcolor: '#eff6ff', p: 1, borderRadius: 2 }}>
              <Typography color="primary" variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1, ml: 1 }}>
                {selected.length} items selected
              </Typography>
              <Tooltip title="Delete Selection">
                <IconButton onClick={handleBulkDelete} size="small" sx={{ color: '#ef4444' }}><FiTrash2 /></IconButton>
              </Tooltip>
            </Box>
          ) : (
            <>
              <TextField
                placeholder="Search by name or email..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (<InputAdornment position="start"><FiSearch color="#94a3b8" /></InputAdornment>),
                  sx: { borderRadius: 2, bgcolor: '#f8fafc', '& fieldset': { border: 'none' } }
                }}
                sx={{ flexGrow: 1, maxWidth: 400 }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  select
                  size="small"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ width: 150, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' }, '& fieldset': { border: 'none' } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><FiFilter size={16} /></InputAdornment> }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </TextField>
                <Button variant="outlined" startIcon={<FiDownload />} onClick={handleExport} sx={{ borderRadius: 2, borderColor: '#e2e8f0', color: '#64748b' }}>
                  Export
                </Button>
                <Tooltip title="Refresh Data">
                    <IconButton onClick={loadUsers}><FiRefreshCw /></IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </Toolbar>

        {/* Table Content */}
        <TableContainer className="custom-scroll" sx={{ maxHeight: 650 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow className="modern-table-head">
                <TableCell padding="checkbox">
                  <Checkbox 
                    indeterminate={selected.length > 0 && selected.length < filteredUsers.length}
                    checked={filteredUsers.length > 0 && selected.length === filteredUsers.length}
                    onChange={(e) => setSelected(e.target.checked ? filteredUsers.map(n => n.id) : [])}
                    size="small"
                  />
                </TableCell>
                <TableCell><TableSortLabel active={orderBy === 'name'} direction={order} onClick={() => handleRequestSort('name')}>User Profile</TableSortLabel></TableCell>
                <TableCell>Email Address</TableCell>
                <TableCell>Enrolled Course</TableCell>
                <TableCell><TableSortLabel active={orderBy === 'status'} direction={order} onClick={() => handleRequestSort('status')}>Status</TableSortLabel></TableCell>
                <TableCell><TableSortLabel active={orderBy === 'created_at'} direction={order} onClick={() => handleRequestSort('created_at')}>Date Added</TableSortLabel></TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(rowsPerPage)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell padding="checkbox"><Skeleton variant="rectangular" width={18} height={18} /></TableCell>
                    <TableCell><Box sx={{ display: 'flex', gap: 2 }}><Skeleton variant="circular" width={40} height={40} /><Box><Skeleton width={100} /><Skeleton width={60} /></Box></Box></TableCell>
                    <TableCell><Skeleton width={150} /></TableCell>
                    <TableCell><Skeleton width={120} /></TableCell>
                    <TableCell><Skeleton width={80} /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                    <TableCell align="right"><Skeleton width={20} /></TableCell>
                  </TableRow>
                ))
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.5 }}>
                      <FiSearch size={48} style={{ marginBottom: 16 }} />
                      <Typography variant="h6">No users found</Typography>
                      <Typography variant="body2">Try adjusting your filters or search terms.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                stableSort(filteredUsers, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user) => {
                    const isSelected = selected.includes(user.id);
                    return (
                      <TableRow 
                        key={user.id} 
                        className="hover-row" 
                        selected={isSelected}
                        onClick={(e) => {
                            if (!e.target.closest('.MuiCheckbox-root') && !e.target.closest('button')) {
                                setSelectedUser(user);
                                setDrawerOpen(true);
                            }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox 
                            checked={isSelected} 
                            onClick={(e) => {
                                e.stopPropagation();
                                const newSelected = isSelected ? selected.filter(id => id !== user.id) : [...selected, user.id];
                                setSelected(newSelected);
                            }} 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: stringToColor(user.name), width: 40, height: 40, fontSize: '1rem', fontWeight: 600 }}>
                              {user.name.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} color="#0f172a">{user.name}</Typography>
                              <Typography variant="caption" color="text.secondary">ID: #{user.id.toString().padStart(4, '0')}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: '#475569' }}>{user.email}</TableCell>
                        <TableCell sx={{ color: '#475569' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label={user.course_name || 'N/A'} size="small" variant="outlined" sx={{ borderRadius: 1, borderColor: '#e2e8f0', bgcolor: '#f8fafc' }} />
                            </Box>
                        </TableCell>
                        <TableCell>{getStatusBadge(user.status)}</TableCell>
                        <TableCell sx={{ color: '#64748b' }}>{new Date(user.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); setMenuUserId(user.id); }}>
                            <FiMoreHorizontal />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[7, 10, 25]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
        />
      </Paper>

      {/* 3. Advanced Drawer Details */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 450, borderRadius: '16px 0 0 16px',  top: '65px',  height: 'calc(100% - 44px)', } }}>
        {selectedUser && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)', borderBottom: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Avatar sx={{ width: 72, height: 72, bgcolor: stringToColor(selectedUser.name), fontSize: '1.75rem', mb: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  {selectedUser.name.charAt(0)}
                </Avatar>
                <IconButton onClick={() => setDrawerOpen(false)} sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#f1f5f9' } }}><FiChevronRight /></IconButton>
              </Box>
              <Typography variant="h5" fontWeight={700}>{selectedUser.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{selectedUser.email}</Typography>
              {getStatusBadge(selectedUser.status)}
            </Box>

            {/* Tabs */}
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tab label="Overview" icon={<FiUser />} iconPosition="start" />
              <Tab label="Activity" icon={<FiActivity />} iconPosition="start" />
            </Tabs>

            {/* Tab Content */}
            <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
              {tabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Course Details</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <FiCheckCircle color="#10b981" />
                        <Typography fontWeight={500}>{selectedUser.course_name || "No Course Assigned"}</Typography>
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">Joined</Typography>
                      <Typography fontWeight={500} sx={{ mt: 0.5 }}>{new Date(selectedUser.created_at).toLocaleDateString()}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">User ID</Typography>
                      <Typography fontWeight={500} sx={{ mt: 0.5 }}>#{selectedUser.id}</Typography>
                    </Paper>
                  </Grid>
                  
                  {/* Quick Actions */}
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Quick Actions</Typography>
                    {selectedUser.status === 'pending' && (
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <Button fullWidth variant="contained" color="success" startIcon={<FiCheckCircle />} onClick={() => handleAction(selectedUser.id, "approve")}>Approve</Button>
                            <Button fullWidth variant="outlined" color="warning" startIcon={<FiXCircle />} onClick={() => handleAction(selectedUser.id, "reject")}>Reject</Button>
                        </Box>
                    )}
                    <Button fullWidth variant="outlined" color="error" startIcon={<FiTrash2 />} onClick={() => setConfirmDialog({ open: true, id: selectedUser.id, type: 'delete' })}>Delete Account</Button>
                  </Grid>
                </Grid>
              )}
              {tabValue === 1 && (
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: 0.7 }}>
                     <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Recent system logs</Typography>
                     {[1,2,3].map(i => (
                         <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                             <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#cbd5e1' }} />
                             <Box>
                                <Typography variant="body2" fontWeight={500}>Login attempt successful</Typography>
                                <Typography variant="caption" color="text.secondary">2 hours ago</Typography>
                             </Box>
                         </Box>
                     ))}
                 </Box>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* 4. Dialogs & Menus */}
      <Menu 
        anchorEl={anchorEl} 
        open={Boolean(anchorEl)} 
        onClose={() => setAnchorEl(null)}
        PaperProps={{ elevation: 0, sx: { border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 150 } }}
      >
        <MenuItem onClick={() => handleAction(menuUserId, "approve")}><FiCheckCircle style={{ marginRight: 8, color: '#10b981' }} /> Approve</MenuItem>
        <MenuItem onClick={() => handleAction(menuUserId, "reject")}><FiXCircle style={{ marginRight: 8, color: '#f59e0b' }} /> Reject</MenuItem>
        <Divider />
        <MenuItem onClick={() => { setConfirmDialog({ open: true, id: menuUserId, type: 'delete' }); setAnchorEl(null); }} sx={{ color: '#ef4444' }}><FiTrash2 style={{ marginRight: 8 }} /> Delete</MenuItem>
      </Menu>

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><FiAlertCircle color="#ef4444" size={24} /> Confirm Action</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete this user? This action cannot be undone.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button onClick={() => handleAction(confirmDialog.id, "delete")} variant="contained" color="error" autoFocus>Delete User</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.type} variant="filled" sx={{ borderRadius: 2, fontWeight: 500 }}>{snackbar.msg}</Alert>
      </Snackbar>

    </Box>
  );
};

export default UserTable;